import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import DiscordButton from "@/components/DiscordButton";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created! Setting up your demo servers…");
      nav("/app");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-black grid-bg flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="w-full max-w-md relative">
        <Link to="/" className="flex items-center justify-center gap-2 font-head font-black text-2xl mb-8">
          <BrandLogo className="w-8 h-8" /> crank<span className="text-volt">.live</span>
        </Link>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
          <h1 className="font-head text-2xl font-bold mb-1">Create account</h1>
          <p className="text-zinc-500 text-sm mb-6">We'll pre-load demo servers so you can explore instantly.</p>
          <DiscordButton />
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400">Name</label>
              <input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required
                className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-4 py-2.5 focus:border-volt outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400">Email</label>
              <input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-4 py-2.5 focus:border-volt outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400">Password</label>
              <input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-4 py-2.5 focus:border-volt outline-none" />
            </div>
            <button data-testid="register-submit" disabled={loading} className="w-full bg-volt text-volt-ink font-semibold rounded-md py-2.5 hover:bg-volt-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
            </button>
          </form>
          <p className="text-sm text-zinc-500 mt-6 text-center">Have an account? <Link to="/login" className="text-volt hover:underline">Log in</Link></p>
        </div>
      </div>
    </div>
  );
}
